import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * UI 上下文（玄墨鎏金主题 + 顶部 Toast）
 * - theme：dark = 玄墨鎏金（默认）/ light = 宣纸鎏金，写入 html[data-theme] 并持久化
 * - toast：任意页面调用 showToast(msg) 在导航栏下方弹出提示（1.8s 自动消失）
 */

export type Theme = 'dark' | 'light';

interface UIContextValue {
  theme: Theme;
  toggleTheme: () => void;
  showToast: (msg: string) => void;
}

const UIContext = createContext<UIContextValue | null>(null);

const THEME_KEY = 'suangua-theme';
const TOAST_DURATION = 1800;

/** 读取初始主题（与 index.html 内联脚本同 key，保持一致） */
function initialTheme(): Theme {
  try {
    const t = localStorage.getItem(THEME_KEY);
    if (t === 'light' || t === 'dark') return t;
  } catch {
    /* 静默降级 */
  }
  return 'dark';
}

export function UIProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 主题变化 → 写入根节点 data-theme + localStorage（CSS 变量随动切换）
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* 静默降级 */
    }
  }, [theme]);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), TOAST_DURATION);
  }, []);

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    showToast(next === 'dark' ? '已切换 · 玄墨鎏金（暗）' : '已切换 · 宣纸鎏金（亮）');
  }, [theme, showToast]);

  const value = useMemo(() => ({ theme, toggleTheme, showToast }), [theme, toggleTheme, showToast]);

  return (
    <UIContext.Provider value={value}>
      {children}
      {/* 顶部 Toast（桌面：导航栏下方居中） */}
      {toastMsg && <div className="toast">{toastMsg}</div>}
    </UIContext.Provider>
  );
}

/** 使用 UI 上下文（必须在 UIProvider 内） */
export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI 必须在 UIProvider 内使用');
  return ctx;
}

/** 主题名称展示（原型 chip 文案） */
export function themeLabel(theme: Theme): string {
  return theme === 'dark' ? '玄墨鎏金' : '宣纸鎏金';
}
